import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';
import ItemList from 'flarum/common/utils/ItemList';
import Button from 'flarum/common/components/Button';
import LogInButtons from 'flarum/forum/components/LogInButtons';
import Composer from 'flarum/forum/components/Composer';
import AuthButtons from './components/AuthButtons';
import { tryWechatAutoLogin } from './utils/wechat';
import { installTextPreviewFix } from './utils/textPreviewFix';
import m from 'mithril';
import ClassFooterNav from './components/ClassFooterNav';
import ClassHubHero from './components/ClassHubHero';
import GuestGuard from './components/GuestGuard';
import installDiscussionLock from './components/DiscussionLock';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import SpeakButton from './components/SpeakButton';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import icon from 'flarum/common/helpers/icon';
import { loadTags } from './utils/tagsSource';
import { excerptOf, relativeTime, absoluteTime } from './utils/discussionExcerpt';

/*
 * 在 mithril vdom 里递归找第一个满足条件的节点。
 *
 * 用来定位列表项的 .DiscussionListItem-content（要把时间挂在它上面）。
 * children 可能是数组、单个 vnode 或字符串，这里统一兼容；
 * 找不到就返回 null，调用方直接放弃（降级为不显示时间，不报错）。
 */
function findVnode(node, pred)
{
  if (!node || typeof node !== 'object') {
    return null;
  }

  if (pred(node)) {
    return node;
  }

  const children = node.children;

  if (!children || typeof children !== 'object') {
    return null;
  }

  const list = Array.isArray(children) ? children : [children];

  for (let i = 0; i < list.length; i++) {
    const found = findVnode(list[i], pred);

    if (found) {
      return found;
    }
  }

  return null;
}

/*
 * Flarum 1.8 的注册入口是 SignUpModal（弹窗），不是独立的 RegisterPage 页面。
 * 核心的 SignUpModal.body() 与 LogInModal 都会渲染 <LogInButtons />，
 * 因此只需要扩展 LogInButtons.prototype.items 一次，
 * 登录弹窗 + 注册弹窗就会同时出现「微信登录」和「手机短信登录」按钮。
 *
 * 注意：必须包在 app.initializers.add() 里。
 * Flarum 只会把扩展打包产物挂到 flarum.extensions[...]，
 * 只有 new-style 的 `extend` 导出（Extend.* extender）会被 bootExtensions 自动执行，
 * 单纯的 `export default function` 永远不会被调用。
 */
/*
 * 【排序下拉按钮】（1.8.36.11）
 *
 * 【1.8.36.10 为什么改不动文字】核心的 SelectDropdown（flarum/common/components/SelectDropdown）
 * 的按钮文字**根本不读 attrs.label**。它的 getButtonContent() 源码里写的是：
 *     const active = children.find(isActive);
 *     const content = (active && active.children) || this.attrs.defaultLabel;
 *     return [m('span.Button-label', content), icon(caretIcon, {className:'Button-caret'})];
 * 也就是「显示当前选中那一项的文字」，label 属性完全没被用到 ——
 * 所以上版传 `label:'排序'` 没有任何效果，按钮依旧显示「最新回复」。
 *
 * 这里继承它、只覆写 getButtonContent()，按钮文字固定成「排序」；
 * 下拉菜单项和点击事件仍完全由核心提供（含当前项打勾），行为没有变化。
 */
class AcmeSortDropdown extends SelectDropdown {
  getButtonContent() {
    return [
      m('span.Button-label', '排序'),
      this.attrs.caretIcon ? icon(this.attrs.caretIcon, { className: 'Button-caret' }) : null,
    ];
  }
}

app.initializers.add('acme/flarum-auth-sms-wechat', () => {
  extend(LogInButtons.prototype, 'items', function (items) {
    // 优先级 -100：如果以后装了 fof/oauth 之类同样往这里塞按钮的扩展，
    // 我们这块会排在它们后面，不会把别人的按钮挤到中间。
    items.add('acme-wechat-sms', <AuthButtons />, -100);
  });

  /*
   * 游客打开帖子详情页 /d/xxx 时，不再整页跳转（1.6.x 的 302 会把整个页面
   * 换成首页）。改成页面停在原地址、正文区显示「内容已锁定」卡片 + 弹一次提示。
   * 这里的 override 不读 app.session，可以同步执行。
   */
  installDiscussionLock();

  /*
   * 帖子列表项改造（1.8.9）：
   *   ① 去掉核心那条「用户名 发布于 X 时间」（infoItems 里的 terminalPost 项）
   *   ② 去掉标签（主标签 + 次标签都不要，key 是 'tags'）
   *   ③ 换成首帖内容预览（纯文本，1.8.11 起最长 30 字）
   *   ④ 发布时间紧贴【回复数的下面】（见下面对 view() 的扩展）
   *
   * 【关键 A】首帖正文默认不在列表 API 的响应里，
   * 必须让请求带上 include=firstPost，否则 discussion.firstPost() 恒为 undefined，
   * 预览永远渲染不出来（不报错，只是空白 —— 很不好查）。
   *
   * 【关键 B】ItemList 的优先级是【数字大的排前面】（实测 DOM 顺序：
   * acmeTime(40) → acmeExcerpt(15) → tags(10)，即降序）。
   * 想让「摘要在左、时间在最右」，摘要的优先级必须【大于】时间。
   */
  extend(IndexPage.prototype, 'requestParams', function (params) {
    const include = Array.isArray(params.include) ? params.include : [];

    if (include.indexOf('firstPost') === -1) {
      include.push('firstPost');
    }

    params.include = include;
  });

  /*
   * 【「发布主题」搬进 .IndexPage-toolbar】（1.8.36.9）
   *
   * 核心列表页顶上有两条互不相干的容器：
   *   sidebarItems() -> nav.IndexPage-nav.sideNav > ul
   *        里面是 newDiscussion（铅笔按钮，li 带 App-primaryControl）
   *        和侧栏导航下拉（App-titleControl）
   *   toolbarItems() -> div.IndexPage-toolbar
   *        > ul.IndexPage-toolbar-view   （排序下拉）
   *        > ul.IndexPage-toolbar-action （刷新 / 全部已读，float:right）
   *
   * 手机端核心把带 .App-primaryControl / .App-titleControl 的元素
   * position:absolute 甩到顶栏（@phone: top:0），
   * 所以「发布主题」跟 .IndexPage-toolbar 这一行的东西根本不在同一个容器里 ——
   * 1.8.36.7 / .8 只靠 CSS 挪坐标、改观感，怎么调都跟 toolbar 里的元素对不齐，
   * 而且核心那条 `.App-primaryControl>.Button .Button-icon{display:block}`
   * 会把按钮挤成「图标一行 + 文字一行」。
   *
   * 这次直接在数据层换容器：从 sidebarItems 摘掉 newDiscussion，
   * 按核心原样重建一个按钮，挂到 actionItems 里
   * （=> ul.IndexPage-toolbar-action），
   * 它就自然落在 .IndexPage-toolbar 里、和排序下拉同一行靠右，不用再写定位 CSS。
   *
   * 【为什么不沿用 li.App-primaryControl 那两个类名】
   * 带上就会被核心 @phone 规则再次绝对定位回顶栏，等于白改。
   * 这里换成自有的 .AcmeToolbar-newDiscussion，样式写在 resources/less/forum.less。
   *
   * 【TagsPage 例外】flarum-tags 的标签磁贴首页（TagsPage）继承 IndexPage，
   * 但它渲染的是 ul.TagTiles、**没有 toolbar**，
   * 那边如果也摘掉就彻底没有发布入口了 —— 所以磁贴页保持原样（右上角抽屉按钮）。
   * 判据：只有 TagsPage 有 tagTileListView 这个方法。
   */
  override(IndexPage.prototype, 'sidebarItems', function (original) {
    const items = original();

    // TagsPage（标签磁贴）没有 toolbar，别动它 —— 否则那一页失去发布入口。
    if (typeof this.tagTileListView === 'function') {
      return items;
    }

    if (typeof items.remove === 'function') {
      items.remove('newDiscussion');
    }

    return items;
  });

  override(IndexPage.prototype, 'actionItems', function (original) {
    const items = original();

    /*
     * 1.8.36.10：刷新图标搬到排序下拉后面（见下面 viewItems），「全部已读」直接不渲染。
     * 这里是从 ItemList 里摘掉，不是 CSS 隐藏 —— DOM 里根本不出现，也省掉一次权限判断。
     */
    if (typeof items.remove === 'function') {
      items.remove('refresh');
      items.remove('markAllAsRead');
    }

    // 复刻核心 sidebarItems 里的判断：不能发帖时按钮 disabled，文案换成 cannot_…
    const canStart = app.forum.attribute('canStartDiscussion') || !app.session.user;

    items.add(
      'acmeNewDiscussion',
      m(Button, {
        icon: 'fas fa-edit',
        className: 'Button Button--primary AcmeToolbar-newDiscussion',
        itemClassName: 'AcmeToolbar-newDiscussion-item',
        onclick: () => this.newDiscussionAction().catch(() => {}),
        disabled: !canStart,
      }, app.translator.trans('core.forum.index.' + (canStart ? 'start_discussion_button' : 'cannot_start_discussion_button'))),
      // -100：排在核心的 refresh / markAllAsRead 之后 —— ItemList 数字大的排前面。
      -100
    );

    return items;
  });

  /*
   * 【列表工具条改造】（1.8.36.10）
   *  ① 排序下拉的按钮文字固定显示「排序」，不再跟着当前排序项变（原来会显示「最新回复」等）
   *  ② 刷新图标挪到排序下拉后面（同一个 ul.IndexPage-toolbar-view 里，紧挨着）
   *  ③ 「全部已读」直接删掉（见上面 actionItems 里的 remove）
   *
   * 【为什么要整个重建 sort 而不是改文字】
   * 核心 viewItems 里那个 SelectDropdown 的 label 是
   * `sortMap[当前排序]` 的翻译值，下拉里的四个选项也是它内部生成的 ——
   * 组件没有暴露「只改 label」的口子，只能 remove('sort') 后按核心原样重建一个，
   * 只把 label 换成固定文案。选项、点击事件、勾选状态全部复刻核心实现，行为不变。
   */
  override(IndexPage.prototype, 'viewItems', function (original) {
    const items = original();

    const sortMap = app.discussions.sortMap() || {};
    const sortKeys = Object.keys(sortMap);
    const current = app.search.params().sort || sortKeys[0];

    if (typeof items.remove === 'function') {
      items.remove('sort');
    }

    if (sortKeys.length) {
      items.add(
        'sort',
        m(AcmeSortDropdown, {
          buttonClassName: 'Button',
          defaultLabel: '排序',
          accessibleToggleLabel: app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label'),
        }, sortKeys.map(function (key) {
          const active = current === key;

          return m(Button, {
            icon: active ? 'fas fa-check' : true,
            onclick: app.search.changeSort.bind(app.search, key),
            active: active,
          }, app.translator.trans('core.forum.index_sort.' + key + '_button'));
        })),
        // 100：与核心一致，排在 refresh(90) 前面 —— ItemList 数字大的排前面。
        100
      );
    }

    items.add(
      'refresh',
      m(Button, {
        title: app.translator.trans('core.forum.index.refresh_tooltip'),
        icon: 'fas fa-sync',
        className: 'Button Button--icon',
        onclick: function () {
          app.discussions.refresh();

          if (app.session.user) {
            app.store.find('users', app.session.user.id());
          }

          m.redraw();
        },
      }),
      90
    );

    return items;
  });

  /*
   * 【朗读按钮诊断】（1.8.36.19）
   *
   * 手机微信里没法开控制台，所以在地址后加 `?acmeDiag=1` 就能弹一段诊断文本。
   * 用法：打开任意帖子页 -> 地址栏末尾加 ?acmeDiag=1 -> 刷新 -> 弹出 alert。
   * 用来区分「按钮没渲染（环境不支持 / 未登录）」和「渲染了但被 CSS 藏了/挤出屏幕」。
   */
  function acmeDiagEnabled() {
    try {
      return window.location.search.indexOf('acmeDiag=1') >= 0 || window.location.hash.indexOf('acmeDiag') >= 0;
    } catch (e) {
      return false;
    }
  }

  function acmeDiagReport(user) {
    if (window.__acmeSpeakDiagDone) {
      return;
    }

    window.__acmeSpeakDiagDone = true;

    setTimeout(function () {
      const lines = [];
      const hasSynth = typeof window.speechSynthesis !== 'undefined';
      const hasUtt = typeof window.SpeechSynthesisUtterance !== 'undefined';

      lines.push('UA: ' + (navigator.userAgent || ''));
      lines.push('宽高: ' + window.innerWidth + 'x' + window.innerHeight);
      lines.push('登录: ' + (user ? user.username() + ' #' + user.id() : '游客（按钮不会渲染）'));
      lines.push('speechSynthesis: ' + hasSynth + ' / Utterance: ' + hasUtt);

      let voices = [];
      try {
        voices = (hasSynth && window.speechSynthesis.getVoices()) || [];
      } catch (e) {
        voices = [];
      }

      let zh = false;
      for (let i = 0; i < voices.length; i++) {
        if (/^zh|^cmn/i.test(voices[i].lang || '')) {
          zh = true;
        }
      }

      lines.push('语音数: ' + voices.length + ' / 含中文: ' + zh);

      const nav = document.querySelector('.DiscussionPage-nav > ul');
      if (nav) {
        const names = [];
        for (let i = 0; i < nav.children.length; i++) {
          names.push(nav.children[i].className || '(无名)');
        }
        lines.push('nav li: ' + names.join(' | '));
      } else {
        lines.push('nav li: （找不到 .DiscussionPage-nav > ul）');
      }

      const btn = document.querySelector('.AcmeSpeak');
      lines.push('朗读按钮: ' + (btn ? '存在' : '不存在'));

      if (btn) {
        const r = btn.getBoundingClientRect();
        const cs = window.getComputedStyle(btn);
        lines.push('位置: x=' + Math.round(r.left) + ' y=' + Math.round(r.top) + ' 宽=' + Math.round(r.width) + ' 高=' + Math.round(r.height));
        lines.push('样式: display=' + cs.display + ' visibility=' + cs.visibility + ' opacity=' + cs.opacity);
        lines.push('是否超出屏幕: ' + (r.width === 0 || r.height === 0 || r.left >= window.innerWidth ? '是' : '否'));
      }

      window.alert('[朗读诊断 1.8.36.19]\n' + lines.join('\n'));
    }, 1500);
  }

  /*
   * 【朗读主帖】（1.8.36.15 起，位置几经调整，1.8.36.19 定稿为「紧挨关注按钮」）
   *
   * 【挂点】nav.DiscussionPage-nav > ul
   * 从线上 bundle 取证：这个 ul 的数据源是 DiscussionPage.prototype.sidebarItems()
   * （**不是 navItems** —— navItems 是个人主页 UserPage 的），
   *   sidebar() -> m('nav.DiscussionPage-nav', m('ul', sidebarItems().toArray()))
   * 这一行里各项的优先级（ItemList 数字大的排前面）：
   *   controls = 100       核心，回复 / 主题菜单
   *   subscription = 80    flarum-subscriptions 扩展，关注按钮
   *   scrubber = -100      核心，「1 / 2 条」进度
   *
   * 【位置版本反复记录】
   *   .16 = -110（最右）→ .17 = 79（紧挨关注）→ .18 = -110（最右，不理想，用户回退）
   *   → .19 = **79（紧挨关注按钮，定稿）**。想放回最右就把 79 换成 -110，别再从头试。
   *
   * 【手机端为什么看不见（1.8.36.19 修）】
   * 不是 CSS 的问题（核心 @phone 只把 item-controls / item-scrubber 改成 display:block，
   * 其余 li 都是 inline-block，我们的 li 正常参与排版）。
   * 真因在组件里：1.8.36.15~.18 的 SpeakButton.view() 写了
   *   `if (!this.supported) return null;`
   * 手机微信（Android X5 内核）里 window.speechSynthesis 常常压根不存在 -> 按钮整个不渲染；
   * 电脑版微信是 Chromium 内核，API 齐全 -> 能看到。所以现象是「只有手机端没有按钮」。
   * 现在改成按钮永远渲染，不支持时点击给提示（speak_unsupported）。
   *
   * 【为什么不放在楼层 actionItems】那会让每条回复都出现一颗朗读按钮，需求只要一个入口。
   * 只读首楼正文，实现全在 components/SpeakButton.js（浏览器内置 speechSynthesis，零后端）。
   *
   * 游客不加按钮：正文被 ContentGuard 锁着，取不到文本。诊断里会写明是游客。
   */
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    const user = app.session.user;

    if (acmeDiagEnabled()) {
      acmeDiagReport(user);
    }

    if (!user) {
      return;
    }

    items.add('acmeSpeak', m(SpeakButton, { discussion: this.discussion }), 79);
  });

  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    const discussion = this.attrs.discussion;

    if (!discussion) {
      return;
    }

    if (typeof items.remove === 'function') {
      items.remove('terminalPost');
      items.remove('tags');
    }

    // 数字大的排前面，摘要放在最左。1.8.11：摘要 20 字 → 30 字。
    const excerpt = excerptOf(discussion, 30);

    if (excerpt) {
      items.add('acmeExcerpt', m('span.AcmeDiscussion-excerpt', excerpt), 30);
    }
  });

  /*
   * 发布时间放到【回复数的下面】，并且紧贴着它（1.8.11）。
   *
   * 【为什么不能放在 infoItems 里】info 那一行在标题下方、靠左，
   * 而回复数 .DiscussionListItem-count 是另一块元素 ——
   * 两者不在同一个容器里，靠 CSS 顺序/优先级怎么调都到不了它下面。
   *
   * 【1.8.10 的做法为什么行间距大】当时把时间挂到 .DiscussionListItem-content
   * 上、用 bottom 定位。content 的高度随标题行数/字号变化（老年友好大字号下
   * 标题一高，时间就被顶到列表底部），而回复数始终在顶部 margin-top:12px，
   * 于是两者的间距会被标题高度撑开 —— 标题越大，间距越大。
   *
   * 【1.8.11 的做法】把时间直接塞进 .DiscussionListItem-count 这个元素内部，
   * 用 top:100% 定位：100% 就是回复数盒子自身的高度，所以时间永远紧贴
   * 回复数下沿，跟标题字号、行数完全无关（样式见 resources/less/forum.less）。
   *
   * 找不到回复数节点就退回挂到 content 上（老行为），不报错。
   */
  extend(DiscussionListItem.prototype, 'view', function (vdom) {
    const discussion = this.attrs.discussion;

    if (!discussion) {
      return;
    }

    const at = discussion.lastPostedAt() || discussion.createdAt();

    if (!at) {
      return;
    }

    const timeNode = m('span.AcmeDiscussion-cornerTime', { title: absoluteTime(new Date(at).getTime()) }, relativeTime(at));

    // 优先挂到回复数元素里（间距最小、最稳）
    const count = findVnode(vdom, function (node) {
      return !!(node.attrs && typeof node.attrs.className === 'string'
        && node.attrs.className.indexOf('DiscussionListItem-count') !== -1);
    });

    if (count) {
      if (!Array.isArray(count.children)) {
        count.children = count.children ? [count.children] : [];
      }

      count.children.push(timeNode);

      return;
    }

    // 兜底：没找到回复数就退回挂到 content 上
    const content = findVnode(vdom, function (node) {
      return !!(node.attrs && typeof node.attrs.className === 'string'
        && node.attrs.className.indexOf('DiscussionListItem-content') !== -1);
    });

    if (!content || !Array.isArray(content.children)) {
      return;
    }

    content.children.push(timeNode);
  });

  // 微信内静默自动登录。
  // 用 setTimeout 推到下一个宏任务：initializer 是在 app.boot() 期间同步执行的，
  // 此时 app.session.user 还没就绪，直接判断会误判成「未登录」。
  setTimeout(tryWechatAutoLogin, 0);

  /*
   * fof/upload「文本文档预览」救活补丁（1.8.36.35）：
   * fof/upload 把「展开全文」的监听写在正文里的内联 <script>，而 Flarum 用
   * innerHTML 注入 contentHtml —— 内联脚本不执行 → 按钮看得见、点了没反应。
   * 这里用 document 级事件委托接管（详情见 utils/textPreviewFix.js 头注释）。
   */
  installTextPreviewFix();

  // 手机端首页「一屏着陆页」（绿色主题）。
  //
  // 刻意【不用】 extend(IndexPage.prototype,'view') 注入：后台「外观 → 首页」可以把
  // Tags 页（班级主标签列表）等设为默认首页，那时 IndexPage 根本不会渲染，
  // 注入也就彻底失效。这里改成单独挂一个 mithril 根节点到 body（与底部班级导航同理），
  // 由组件内部用 URL 路径判断是否为首页（m.route.get() === '/'），
  // 无论首页实际渲染的是 IndexPage 还是 TagsPage 都生效。
  const landingRoot = document.createElement('div');
  landingRoot.className = 'AcmeLanding-root';
  document.body.appendChild(landingRoot);
  m.mount(landingRoot, ClassHubHero);

  /*
   * 下面这两件事都【必须】推迟到下一个宏任务（app.boot() 之后）执行。
   *
   * initializer 是在 app.boot() 期间同步跑的，此刻 app.session 还没被赋值
   * （实测为 undefined）。ClassFooterNav.view() 一旦读 app.session.user 就抛
   *   TypeError: Cannot read properties of undefined (reading 'user')
   * 异常发生在 initializer 内部 → 整个扩展 "failed to initialize"，
   * 底部导航永远不会出现。用 setTimeout 推到 boot 之后就安全了
   * （与上面的 tryWechatAutoLogin 同一个道理）。
   */
  setTimeout(() => {
    /*
     * 统一拉取一次标签数据。
     *
     * 【踩坑】这里【不要】用 app.store.find('tags') / app.store.all('tags')：
     * 实测在首页（后台把首页设成 Tags 页时尤其明显）store 里的标签常常为空
     * 或只有主标签，导致班级/次标签全部匹配失败 —— 表现就是着陆页四张卡片的
     * href 全部退化成 '/'，点下去毫无反应。
     *
     * 改用 utils/tagsSource.js 自己请求 /api/tags（带缓存，全站只请求一次），
     * 拿到后重绘，着陆页卡片和底部导航都会自动更新。
     */
    loadTags().then(
      () => m.redraw(),
      () => {}
    );

    // 登录用户底部班级导航：按所属班级动态展示该班的 4 个次标签。
    // 单独挂一个 mithril 根节点到 body，Flarum 的 m.redraw()（登录/路由变化时触发）
    // 会一并重绘它，因此登录后导航会自动出现、切换账号也会刷新。
    const navRoot = document.createElement('div');
    navRoot.className = 'AcmeClassNav-root';
    document.body.appendChild(navRoot);
    m.mount(navRoot, ClassFooterNav);

    /*
     * 游客内容守卫：未登录用户能浏览列表，点帖子时被拦下并提示
     * 「请尽快找任课老师绑定班级」（见 components/GuestGuard.js）。
     *
     * 同样必须放在 boot 之后：组件内部要读 app.session.user，
     * initializer 同步执行时 app.session 还是 undefined。
     */
    const guardRoot = document.createElement('div');
    guardRoot.className = 'AcmeGuestGuard-root';
    document.body.appendChild(guardRoot);
    m.mount(guardRoot, GuestGuard);
  }, 0);
});

/*
 * 精简编辑器工具栏（1.8.13）
 *
 * 【先纠正一个误解】编辑器里那排按钮**不是 flarum/bbcode 提供的**。
 * flarum/bbcode 的 composer.json 里 js: false，它只有 PHP 端（s9e 解析 BBCode 语法），
 * 一个按钮都没注册。实际按钮全部来自 flarum/markdown 的
 * js/src/common/index.js —— markdownToolbarItems() 里 add 了 11 个 MarkdownButton。
 *
 * 【为什么不能改 vendor】vendor/flarum/markdown 由 composer 管理，改了下次
 * composer update 就丢；而且它的 js 是打进整站 bundle 的，改完要重编核心，不现实。
 * 正确做法：override TextEditor.prototype.markdownToolbarItems，
 * 等它把按钮组装完，再把不想要的 key 删掉。
 *
 * 【顺序坑（关键）】markdown 是 `TextEditor.prototype.markdownToolbarItems = fn`
 * 直接赋值（不是 override）。如果我们的 initializer 比它**先**运行，
 * 它会再 override 我们一层 —— 我们的 remove 就变成在它 add 之前执行，等于白删。
 * 所以这里单独注册一个优先级 -100 的 initializers（ItemList 数字大先执行），
 * 保证最后覆盖。
 *
 * 【保留】quote（引用）—— 1.8.14 起工具栏只剩这一个。
 * 【移除】header / bold / italic / strikethrough / spoiler / code / link /
 *        image / unordered_list / ordered_list
 *
 * 若哪天 markdown 被停用，original 会是 undefined，这里直接返回空 ItemList，
 * 不会报错（工具栏那排按钮本来就不存在）。
 */
const HIDDEN_TOOLBAR_BUTTONS = [
  'header',
  'bold',
  'italic',
  'strikethrough',
  'spoiler',
  'code',
  'link',
  'image',
  'unordered_list',
  'ordered_list',
];

function slimMarkdownToolbar() {
  override(TextEditor.prototype, 'markdownToolbarItems', function (original) {
    const items = typeof original === 'function' ? original.call(this) : new ItemList();

    HIDDEN_TOOLBAR_BUTTONS.forEach((key) => items.remove(key));

    return items;
  });
}

/*
 * 发帖框右上角只留「关闭」、左上角腾给「预览」（1.8.14）
 *
 * 核心 Composer.controlItems() 往右上角那一组里塞了三个按钮：
 *   minimize（最小化，fa-minus —— 它的 itemClassName 是 **App-backControl**，
 *            手机端会被 fixed 到**左上角**）、fullScreen（全屏）、close（关闭）。
 *
 * 老年学员用不到最小化和全屏；而且「最小化」正占着左上角，预览按钮放不进去。
 * 所以这里把前两个删掉，右上角只剩关闭。
 * exitFullScreen（退出全屏）保留 —— 万一进了全屏模式还能出来（它和上面三个互斥）。
 */
function slimComposerControls() {
  extend(Composer.prototype, 'controlItems', function (items) {
    items.remove('minimize');
    items.remove('fullScreen');
  });
}

/*
 * 发帖框底部那一行的改造（1.8.16）
 *
 * 底部 DOM 来自核心 `TextEditor.controlItems()`，它给了两个按钮：
 *   submit  —— <Button icon="fas fa-paper-plane" className="Button Button--primary"
 *               itemClassName="App-primaryControl">{submitLabel}</Button>
 *   preview —— <Tooltip><Button icon="far fa-eye" …/></Tooltip>（只有传了 attrs.preview 才有）
 *
 * 需求：①不要预览按钮；②提交按钮不要图标，只显示「提交」两个字。
 *
 * 【为什么不改 attrs.submitLabel】submitLabel 是按场景翻译的（发讨论是「发布讨论」、
 * 回复是「发表回复」、编辑是「保存」），改它要逐个 composer 覆盖，还得改语言键。
 * 这里直接把整个 submit 内容换掉，所有场景统一叫「提交」，老年学员看得懂。
 *
 * 【onclick 怎么拿】新 Button 得保留原来的提交动作。原 vnode 上就有 attrs.onclick
 * （this.onsubmit.bind(this)），取过来复用；万一以后核心改动取不到，
 * 退回 this.onsubmit.bind(this)，不会出现点了没反应的按钮。
 */
function simplifyComposerFooter() {
  extend(TextEditor.prototype, 'controlItems', function (items) {
    items.remove('preview');

    const original = items.get('submit');
    const onclick = (original && original.attrs && original.attrs.onclick) || this.onsubmit.bind(this);

    items.setContent(
      'submit',
      <Button className="Button Button--primary" itemClassName="App-primaryControl" onclick={onclick}>
        提交
      </Button>
    );
  });
}

/*
 * 提交按钮右下角的兜底定位（1.8.20）
 *
 * 【为什么非要 JS 写内联】1.8.16~1.8.19 都是靠 Less 定位，但始终改不动。
 * 核心有两条 `!important` 规则在压它：
 *   Composer.less(@phone)  .ComposerBody-editor .TextEditor-controls .item-submit { position: absolute !important }  0,3,0
 *   App.less(@phone)       .App-primaryControl { top: 0 !important }                                                 0,1,0
 * 而我们写在扩展 less 里的规则能否压过，取决于「扩展 less 最终排在核心之后」这件事，
 * 一旦编译顺序/合并方式有个闪失就白改。
 *
 * 【内联 important 的地位】CSS 层叠里，作者样式 + !important + 内联 > 任何外部规则，
 * 除了另一条同为内联 important 的写法，不可能被别的样式表压回去。
 * 所以这里（也只在这里）动一次 DOM：给 li 设置 property(值, 'important')。
 *
 * 【副作用可控】只写定位五件套，外观（绿胶囊 / 15px 字）仍交给 Less；
 * mithril 重绘时不会重置手动加的内联样式（vnode 里没有 style 属性就不会 diff 它）。
 *
 * 【1.8.21 为什么又加了一层全局扫描】1.8.20 只在 TextEditor 的 oncreate 里打一次，
 *   只要发帖框是复用已有 DOM、或被别的扩展（fof/upload、bbcode 等）重挂一次的，
 *   那一次就打空了 —— 表现就是「代码确实部署了，但按钮纹丝不动」，非常难查。
 *   这里改成三道保险：
 *     ① TextEditor 的 oncreate / onupdate 各打一次（最快路径，保留）；
 *     ② initializer 里立刻全表扫一次（覆盖「页面加载时 composer 已经在 DOM 里」）；
 *     ③ MutationObserver 盯 body，任何新挂载的 li.item-submit 立刻补打。
 *   全站同时最多一两个 li.item-submit，querySelectorAll 的开销可以忽略。
 */
// 1.8.22 定案：bottom = 5px（1.8.18 定的是 23px，实机比下来 5px 更贴底、更好按）
const SUBMIT_POSITION = {
  position: 'absolute',
  top: 'auto',
  right: '16px',
  bottom: '5px',
  left: 'auto',
};

// 打过标的节点记一笔，避免重复 setProperty；用 WeakSet 是为了不往 DOM 里写属性
const pinnedNodes = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

function applySubmitPosition(el) {
  if (!el || !el.style) {
    return;
  }

  if (pinnedNodes) {
    if (pinnedNodes.has(el)) {
      return;
    }
    pinnedNodes.add(el);
  }

  Object.keys(SUBMIT_POSITION).forEach((prop) => {
    el.style.setProperty(prop, SUBMIT_POSITION[prop], 'important');
  });

  // z-index 不用 important 也够用（比同级工具按钮高就行）
  el.style.setProperty('z-index', 'calc(var(--zindex-header) + 2)');
}

function pinSubmitButtons(scope) {
  const root = scope && scope.querySelectorAll ? scope : document;
  const nodes = root.querySelectorAll('li.item-submit');

  for (let i = 0; i < nodes.length; i++) {
    applySubmitPosition(nodes[i]);
  }

  return nodes.length;
}

/*
 * 「我的文件」按钮（li.item-fof-upload-media）：换文件夹图标 + 去掉底色和边框（1.8.25）
 *
 * 【为什么图标用换 class 而不是 CSS content】Less 里写 content: "\f07b" 要赌编译器对反斜杠的处理
 * （1.8.15 就是在 Less 的转义上栽过）。而 FontAwesome 的 `fas` 已经把 font-family / font-weight
 * 都配好了（站点是 FA5：'Font Awesome 5 Free' + 900），**直接换类名最稳**：
 *   fa-photo-video (\f87c) → fa-folder (\f07b)
 * 而且换 class 不碰 aria-label，「我的文件」的语义和 tooltip 都不受影响。
 *
 * 【为什么底色边框用内联 important】这个按钮的底色/边框不是 fof 加的（fof 那条只有
 * overflow:hidden），来自核心 .Button 的桌面端默认样式与 hover 态。内联 important 是层叠里
 * 最高的，hover 态也压不过它 —— 正好符合「任何时候都不要底色」的诉求。
 * 用 border-color: transparent 而不是 border: 0，是为了不改变按钮占位尺寸。
 */
const UPLOAD_MEDIA_ICON_FROM = 'fa-photo-video';
const UPLOAD_MEDIA_ICON_TO = 'fa-folder';

const UPLOAD_MEDIA_RESET = {
  background: 'transparent',
  'border-color': 'transparent',
  'box-shadow': 'none',
};

// 去掉底色后按钮落在浅色发帖框上，原白图标会看不见 —— 改成浅绿 rgb(171,220,136)（1.8.25）
const UPLOAD_MEDIA_ICON_COLOR = '#abdc88';

// 1.8.36.12：图标放大到 20px（核心 .Button-icon 默认继承按钮字号，手机上偏小看不清）
const UPLOAD_MEDIA_ICON_SIZE = '20px';

const cleanedUploadNodes = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

function cleanUploadMediaButtons(scope) {
  const root = scope && scope.querySelectorAll ? scope : document;
  const items = root.querySelectorAll('li.item-fof-upload-media');

  for (let i = 0; i < items.length; i++) {
    // ① 图标换 class（每次都检查：fof 重绘后类名可能被还原，这里会再换回来）
    const icon = items[i].querySelector('.Button-icon');

    if (icon && icon.classList.contains(UPLOAD_MEDIA_ICON_FROM)) {
      icon.classList.remove(UPLOAD_MEDIA_ICON_FROM);
      icon.classList.add(UPLOAD_MEDIA_ICON_TO);
    }

    // 浅色图标兜底（1.8.25）：透明底落在浅色发帖框上，白图标会看不见
    // 1.8.36.12：同时把图标放大到 20px
    if (icon) {
      icon.style.setProperty('color', UPLOAD_MEDIA_ICON_COLOR, 'important');
      icon.style.setProperty('font-size', UPLOAD_MEDIA_ICON_SIZE, 'important');
    }

    // ② 底色 / 边框清零
    const btn = items[i].querySelector('.Button');

    if (!btn || !btn.style) {
      continue;
    }

    if (cleanedUploadNodes) {
      if (cleanedUploadNodes.has(btn)) {
        continue;
      }
      cleanedUploadNodes.add(btn);
    }

    Object.keys(UPLOAD_MEDIA_RESET).forEach((prop) => {
      btn.style.setProperty(prop, UPLOAD_MEDIA_RESET[prop], 'important');
    });
  }

  return items.length;
}

/*
 * fof 媒体管理器弹窗的标题：去掉 h3（1.8.29）
 *
 * fof/upload 在 js/src/forum/components/FileManagerModal.js 里写死了：
 *   <h3 className="App-titleControl App-titleControl--text">{...media_manager}</h3>
 * 它没把这个组件导出给我们 override（只有 extend.* 和 components 聚合导出），
 * 所以改标签只能动 DOM：把 h3 换成一个普通 div，类名换成我们自己的
 * AcmeFileManagerTitle —— 既去掉 h3 语义，也去掉 fof/核心那两个 class 自带的
 * 标题默认样式，之后外观完全由我们自己的 Less 控制（见 resources/less/forum.less）。
 *
 * 复用 decorateComposer 的三道保险（initializer 全表扫一次 + MutationObserver 长期兜底），
 * 弹窗什么时候挂上来都能改到；替换完 DOM 里再没有 h3，回调自然收敛不会死循环。
 */
const FM_TITLE_SELECTOR = '.fof-file-manager-modal .Modal-header h3.App-titleControl';

function replaceFileManagerTitle(scope) {
  const root = scope && scope.querySelectorAll ? scope : document;
  const nodes = root.querySelectorAll(FM_TITLE_SELECTOR);

  for (let i = 0; i < nodes.length; i++) {
    const h3 = nodes[i];
    const parent = h3.parentNode;

    if (!parent) {
      continue;
    }

    const div = document.createElement('div');

    div.className = 'AcmeFileManagerTitle';
    div.textContent = h3.textContent;
    parent.replaceChild(div, h3);
  }

  return nodes.length;
}

/*
 * 关闭媒体管理器后，把焦点还给原来的发帖框（1.8.31）
 *
 * 【现象】点「取消」或关闭弹窗后，焦点停在 body 上，手机上软键盘收起、光标也不在输入框里，
 *        用户得自己再点一下输入框才能继续打字。
 * 【原因】fof 的弹窗只管开不管还 —— Mithril 把 Modal 节点移除后没有任何 refocus 逻辑，
 *        浏览器也不会自动把焦点交还给打开弹窗前的元素。
 * 【做法】打开前记下焦点元素（capture 阶段的 mousedown，此刻焦点还没从输入框移走），
 *        检测到弹窗消失后把它 focus 回去，并把光标放到文末。
 */
let acmeFocusBeforeModal = null;
let acmeModalWasOpen = false;

function isTextEntry(el) {
  if (!el || !el.tagName) {
    return false;
  }

  const tag = el.tagName.toLowerCase();

  return tag === 'textarea' || tag === 'input' || el.isContentEditable === true;
}

// 键盘打开弹窗时抓不到"打开前的焦点"，用当前发帖框兜底
function findComposerEditor() {
  return document.querySelector('.Composer textarea') || document.querySelector('textarea.TextEditor');
}

function restoreEditorFocus() {
  let el = acmeFocusBeforeModal;

  acmeFocusBeforeModal = null;

  if (!el || el.isConnected === false) {
    el = findComposerEditor();
  }

  if (!el) {
    return;
  }

  const focusIt = () => {
    try {
      el.focus();

      if (typeof el.setSelectionRange === 'function' && typeof el.value === 'string') {
        const end = el.value.length;

        el.setSelectionRange(end, end);   // 光标放到文末，接着原来的内容继续写
      }
    } catch (e) {
      /* 元素已卸载或被隐藏，忽略 */
    }
  };

  // Mithril 关闭弹窗还会重绘一次，晚一点再抢焦点，否则会被重绘顶掉
  setTimeout(focusIt, 30);
}

/*
 * 把「上传」按钮搬进弹窗底部栏，和「取消」排在同一行（1.8.34）
 *
 * 【为什么必须搬 DOM】fof 把上传按钮渲染在 .Modal-content 的最前面
 * （<div class="fof-modal-buttons App-backControl"> 里，位置在 header 之前），
 * 而「取消」在 .Modal-footer 里 —— 两个按钮根本不在同一个容器，
 * 纯 CSS 再怎么定位也只能做到"看起来在同一条线上"，换行/缩放必错。
 * 只有把节点搬进 footer，才是真的一行、且自动对齐、自动等间距。
 *
 * 【为什么搬整个容器而不是只搬 button】容器里还挂着 tooltip 节点，
 * 只搬 button 会把 tooltip 留在原地（提示框飘在弹窗顶部）。整个容器一起搬，tooltip 跟着走。
 *
 * 【搬了会不会坏】按钮里那个 <form><input type=file></form> 是它的文件选择器，
 * 移动节点不会丢监听器（监听器绑在节点上，跟着节点走），点击照样能唤起选择文件。
 * fof 重绘时可能把按钮还原回原处，但下面的 MutationObserver 每轮都会再搬一次。
 */
function moveUploadButtonToFooter(scope) {
  const root = scope && scope.querySelectorAll ? scope : document;
  const modals = root.querySelectorAll('.fof-file-manager-modal');
  let moved = 0;

  for (let i = 0; i < modals.length; i++) {
    const holder = modals[i].querySelector('.fof-modal-buttons');
    const footer = modals[i].querySelector('.Modal-footer');

    if (!holder || !footer || !footer.firstChild) {
      continue;
    }

    // 已经在 footer 里了（footer 的第一个子节点就是它），不用重复搬
    if (holder.parentNode === footer) {
      // 类可能又被 fof 重绘加回来，这里每轮都确保它是摘掉的
      holder.classList.remove('App-backControl');
      continue;
    }

    /*
     * 摘掉核心的 App-backControl 类（1.8.35 根治错位）
     *
     * fof 给这个容器挂了 .App-backControl —— 那是 Flarum **手机端顶部返回按钮**的容器类，
     * 核心给它下了 position:absolute!important / top:0!important / margin:0，
     * 以及 .App-backControl>.Button{height:var(--header-height-phone);padding:13px!important}。
     * 按钮搬进 footer 后，这套"顶栏返回按钮"的样式还在生效，
     * 把上传按钮拉成 46px 高（「取消」只有约 36px）→ 同行不同高，就是用户看到的错位。
     * 摘掉这个类，那套样式整个不适用，按钮回归普通 .Button，与「取消」天然同款。
     * fof 重绘可能又加回来，所以每轮扫描都摘一次（CSS 里另有兜底，双保险）。
     */
    holder.classList.remove('App-backControl');

    footer.insertBefore(holder, footer.firstChild);
    moved++;
  }

  return moved;
}

/*
 * 弹窗层的"点击放行"（1.8.32）
 *
 * CSS 里已经用 .Modal-backdrop:not([data-showing]){pointer-events:none} 修过了，
 * 但核心那个 z-index 写的是 calc(var(--zindex-modal) + var(--modal-count) - 2)，
 * 而本站 --modal-count 没定义 → 整条 calc 失效 → 这层 DOM 顺序上的家伙可能盖住页面。
 * 这层如果还在（有些主题/时机下 CSS 选择器未必命中），就会继续吞点击。
 * 所以每轮扫描都按"里面还有没有活着的弹窗"再内联兜一次。
 */
function syncOverlayBlocking() {
  const layers = document.querySelectorAll('.Modal-backdrop, .ModalManager');

  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];

    if (!layer.style) {
      continue;
    }

    const hasOpenModal = !!layer.querySelector('.Modal.in') || layer.classList.contains('Modal.in');

    layer.style.pointerEvents = hasOpenModal ? 'auto' : 'none';
  }
}

// 每次扫描顺便看一眼弹窗还在不在：从"有"变"无"就是刚被关掉
function syncModalFocusState(scope) {
  const root = scope && scope.querySelector ? scope : document;
  const open = !!root.querySelector('.fof-file-manager-modal');

  if (open) {
    acmeModalWasOpen = true;
    return;
  }

  if (acmeModalWasOpen) {
    acmeModalWasOpen = false;
    restoreEditorFocus();
  }
}

function installFocusMemory() {
  if (typeof document === 'undefined' || !document.addEventListener) {
    return;
  }

  // capture 阶段：点「我的文件」按钮的瞬间，焦点还没从输入框移开 —— 只有这一刻抓得到
  document.addEventListener('mousedown', (e) => {
    const target = e.target;

    if (!target || !target.closest) {
      return;
    }

    if (!target.closest('.item-fof-upload-media') && !target.closest('.fof-upload-button')) {
      return;
    }

    const el = document.activeElement;

    if (isTextEntry(el)) {
      acmeFocusBeforeModal = el;
    }
  }, true);

  // 回车 / 空格打开时焦点已经在按钮上了，抓不到输入框，用当前发帖框兜底
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return;
    }

    const el = document.activeElement;

    if (!el || !el.closest) {
      return;
    }

    if (!el.closest('.item-fof-upload-media') && !el.closest('.fof-upload-button')) {
      return;
    }

    if (!acmeFocusBeforeModal) {
      acmeFocusBeforeModal = findComposerEditor();
    }
  }, true);
}

// 一次扫描里把发帖框 + 媒体管理器弹窗相关的 DOM 改造一次性做完
function decorateComposer(scope) {
  pinSubmitButtons(scope);
  cleanUploadMediaButtons(scope);
  replaceFileManagerTitle(scope);
  moveUploadButtonToFooter(scope);

  // 只在整页扫描时判断弹窗开合，避免在编辑器局部重绘时误判成"弹窗已关闭"
  if (!scope || scope === document) {
    syncOverlayBlocking();
    syncModalFocusState(document);
  }
}

// 一轮 DOM 抖动里可能触发几十次 mutation，用 rAF 合并成一次扫描
let scanQueued = false;

function scheduleSubmitScan() {
  if (scanQueued) {
    return;
  }

  scanQueued = true;

  const run = () => {
    scanQueued = false;
    decorateComposer(document);
  };

  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(run);
  } else {
    setTimeout(run, 0);
  }
}

function pinEditorSubmitButtons(editor) {
  // 不依赖 this.$()：jQuery 万一没挂好也能走原生 DOM
  if (editor && editor.element) {
    pinSubmitButtons(editor.element);
  }
}

extend(TextEditor.prototype, 'oncreate', function (original, ...args) {
  if (typeof original === 'function') {
    original.apply(this, args);
  }

  pinEditorSubmitButtons(this);
});

extend(TextEditor.prototype, 'onupdate', function (original, ...args) {
  if (typeof original === 'function') {
    original.apply(this, args);
  }

  pinEditorSubmitButtons(this);
});

app.initializers.add('acme/flarum-auth-sms-wechat-slim-toolbar', () => {
  slimMarkdownToolbar();
  slimComposerControls();
  simplifyComposerFooter();

  // ② initializer 阶段先全表扫一次 + 装上 MutationObserver 长期兜底
  decorateComposer(document);

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(scheduleSubmitScan).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  installFocusMemory();

  // 排障用：控制台能看到这行，就说明新版 JS 已经跑起来了（版本号跟着发布走）
  if (typeof console !== 'undefined') {
    console.info('[acme-wechat] forum patch 1.8.36.47 applied (toolbar/footer/upload-in-footer/modal/no-h3/focus/overlay/upload-label/sanitize-payload)');
  }
}, -100);
